import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { API_URL } from '@constants';
import { AgendaPoint, Event } from '@models';
import type { MeetingFormResource } from '../../modules/calendar/components/edit-meeting-modal.component';
import { ModalService } from './modal.service';

export interface Geocode {
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

@Service()
export class EventService {
  modalService = inject(ModalService);
  http = inject(HttpClient);

  updateEvent = (rid: number, data: Record<string, unknown>) =>
    this.http.put<Event>(`${API_URL}/events/${rid}`, data);

  getEvent = (rid: number) => this.http.get<Event>(`${API_URL}/events/${rid}`);

  getEvents = (params: Record<string, unknown>) =>
    this.http.get<Event[]>(`${API_URL}/events`, {
      params: params as Record<string, string>,
    });

  getAgendaPointEvent = (rid: number) =>
    this.http.get<Event>(`${API_URL}/agendapoints/${rid}/event`);

  updateAgendaPoint = (rid: number, data: Partial<AgendaPoint>) =>
    this.http.put<AgendaPoint>(`${API_URL}/agendapoints/${rid}`, data);

  updateMinutes = (url: string, params: Partial<Event>) =>
    this.http.put<Event>(`${API_URL}/${url}`, params);

  exportTemplate = (eventId: number, templateId: number): string =>
    `${API_URL}/events/${eventId}/STR.docx?id=${templateId}`;

  copyMeetingFromResource = (
    resourceToCopyRid: number,
    resourceData: Record<string, unknown>
  ) =>
    this.http.post<Event>(
      `${API_URL}/events/${resourceToCopyRid}/copy`,
      resourceData
    );

  getGeocode = (params: { address: string }) =>
    this.http.get<{ results: Geocode[] }>(`/agora-api/geocode`, {
      params,
    });

  getEventStructure = (rid: number) =>
    this.http.get<Event>(`${API_URL}/events/${rid}/structure`);

  replyInvitation = (rid: number, attendance: number) =>
    this.http.post<Event>(`${API_URL}/events/${rid}/invitations`, {
      attendance,
    });

  openAgendaPoint = async (resource: AgendaPoint) => {
    const { AgendaPointModalComponent } =
      await import('../../modules/calendar/components/agenda-point-modal.component');

    return this.modalService.open(
      AgendaPointModalComponent,
      {
        resource,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editAgendaPoint = async (resource: AgendaPoint, eventRid: number) => {
    const { EditAgendaPointModalComponent } =
      await import('../../modules/calendar/components/edit-agenda-point-modal.component');

    return this.modalService.open(
      EditAgendaPointModalComponent,
      {
        resource: cloneDeep(resource),
        eventRid,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editEvent = async (resource: Event) => {
    const { EditEventModalComponent } =
      await import('../../modules/calendar/components/edit-event-modal.component');

    return this.modalService.open(
      EditEventModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  openEventMeeting = async (resource: Event) => {
    const { OnlineMeetingModalComponent } =
      await import('../../modules/calendar/components/online-meeting-modal.component');

    return this.modalService.open(
      OnlineMeetingModalComponent,
      {
        resource,
      },
      {
        windowClass: 'file-modal',
      }
    );
  };

  takeEventMinutes = async (agendapoints: AgendaPoint[], resource: Event) => {
    const { EditMinutesModalComponent } =
      await import('../../modules/calendar/components/edit-minutes-modal.component');

    return this.modalService.open(
      EditMinutesModalComponent,
      {
        agendapoints: cloneDeep(agendapoints),
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  openCopyMeetingModal = async (resourceToCopy: Event) => {
    const { CopyMeetingModalComponent } =
      await import('../../modules/calendar/components/copy-meeting-modal.component');

    return this.modalService.open(
      CopyMeetingModalComponent,
      {
        resourceToCopy: cloneDeep(resourceToCopy),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editMeeting = async (resource: Event) => {
    const { EditMeetingModalComponent } =
      await import('../../modules/calendar/components/edit-meeting-modal.component');

    return this.modalService.open(
      EditMeetingModalComponent,
      {
        resource: cloneDeep(resource) as MeetingFormResource,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  exportFromTemplate = async (pid: number) => {
    const { ExportTemplateModalComponent } =
      await import('../../modules/calendar/components/export-template-modal.component');

    return this.modalService.open(
      ExportTemplateModalComponent,
      {
        pid,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  scheduledNotifications = async (resource: Event) => {
    const { ScheduledNotificationsModalComponent } =
      await import('../../modules/calendar/components/scheduled-notifications-modal.component');

    return this.modalService.open(
      ScheduledNotificationsModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };
}
